import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {TableAction, TableColumn, TableColumnType, TableComponent} from '../../components/table/table.component';
import {MaintenanceManagerService} from '../../services/maintenance-manager.service';
import {Maintenance, MaintenanceStatus, MaintenanceStatusUtils} from '../../models/maintenance';
import {BehaviorSubject, Observable} from 'rxjs';
import {UiManagerService} from '../../services/ui-manager.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Paginator} from '../../models/paginator';
import {Assistance, AssistanceType} from '../../models/assistance';
import {AccountManagerService} from '../../services/account-manager-service';
import {filter, map, tap} from 'rxjs/operators';
import {EventType} from '../../models/calendar-event';
import {environment} from "@environments/environment";
import {TranslatePipe} from "@ngx-translate/core";
import {DEBUG, DebugService} from "../../services/debug.service";
import {Personnel} from "../../models/personnel";

@Component({
  selector: 'app-maintenance-page',
  templateUrl: './maintenance-page.component.html',
  styleUrls: ['./maintenance-page.component.scss']
})
export class MaintenancePageComponent implements OnInit {

  // History
  maintenancesHistory: Maintenance[];
  maintenancesPaginator: Paginator<Maintenance>;

  // Planned maintenances
  plannedMaintenances: Maintenance[];
  plannedMaintenancesPaginator: Paginator<Maintenance>;

  @ViewChild(TableComponent, { static: false })
  historyTable: TableComponent;

  // Live assistance
  maintenances$: BehaviorSubject<Maintenance[]>;
  liveMaintenances$: Observable<Maintenance[]>;
  maintenancesAssignedToMe$: Observable<Maintenance[]>;

  // Current selected maintenance
  selectedMaintenanceId;

  // Is opening maintenance
  isOpeningMaintenanceId: string;

  constructor(
    public accountManager: AccountManagerService,
    private uiManager: UiManagerService,
    public maintenanceManager: MaintenanceManagerService,
    public debugService: DebugService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private translatePipe: TranslatePipe,
  ) { }

  async ngOnInit(): Promise<void> {

    // Wait for login to complete
    await this.accountManager.isLoggedIn()
    this.getMaintenances();

  }

  async getMaintenances() {

    // Maintenance history
    this.getMaintenancesHistory();

    // Planned maintenances if i am field manager
    if (environment.features.fieldManagerManagement && this.accountManager.isTeamLeaderOrAdmin()) {
      this.getPlannedMaintenances();
    }

    // Live maintenances
    this.maintenances$ = this.maintenanceManager.getLiveMaintenances();

    // Configure standard maintenances and planned maintenances assigned to me
    this.configureMaintenancesLists();

  }

  async getMaintenancesHistory() {
    this.maintenancesPaginator = new Paginator<Maintenance>({orderBy: 'closedAt', sorting: "DESC"});

    // Get all completed maintenances for the maintenances history
    this.maintenancesPaginator.onPageResults = (maintenances: Maintenance[]) => {
      this.maintenancesHistory = maintenances;
    }
    await this.maintenanceManager.getMaintenances([3], [], [], this.maintenancesPaginator);
  }

  async getPlannedMaintenances() {
    this.plannedMaintenancesPaginator = new Paginator<Maintenance>({orderBy: 'scheduledAt', sorting: "ASC"});

    // Get all completed maintenances for the maintenances history
    this.plannedMaintenancesPaginator.onPageResults = (maintenances: Maintenance[]) => {
      this.plannedMaintenances = maintenances;
    }
    await this.maintenanceManager.getMaintenances(
      [MaintenanceStatus.QUEUE, MaintenanceStatus.TAKEN, MaintenanceStatus.PLANNED],
      [EventType.PLANNED_MAINTENANCE],
      this.accountManager.isAdmin() ? [] : [ parseInt(this.accountManager.getManagedFieldService().id)],
      this.plannedMaintenancesPaginator
    );
  }

  configureMaintenancesLists() {

    const validMaintenances = this.maintenances$.pipe(
      filter(x => !!x),
      tap(maintenances => {

        /* On maintenance removed */
        if (!maintenances.map(maintenance => maintenance.id).includes(this.selectedMaintenanceId)) {

          /* Reset selected maintenance if was removed */
          this.selectedMaintenanceId = null;

          /* Update maintenance history if a maintenance is closed */
          this.getMaintenancesHistory();

        }

      })
    );

    /* Get live maintenances */
    this.liveMaintenances$ = validMaintenances.pipe(
      map(maintenances => maintenances.filter(maintenance => maintenance.event.type != EventType.PLANNED_MAINTENANCE))
    );

    /* Get maintenances assigned to me */
    this.maintenancesAssignedToMe$ = validMaintenances.pipe(
      map(maintenances => maintenances.filter(maintenance => maintenance.event.type == EventType.PLANNED_MAINTENANCE && maintenance.remoteExpert && maintenance.remoteExpert.id == this.accountManager.account.id))
    );

  }

  getMaintenanceTableColumns(): TableColumn[] {
    return [
      { key: 'id', type: TableColumnType.DEFAULT, title: 'ID', isMasterKey: true },
      { key: 'equipment_name', type: TableColumnType.DEFAULT, title: 'Equipment name' },
      { key: 'asset_serial', type: TableColumnType.DEFAULT, title: 'Serial number' },
      { key: 'field_operator', type: TableColumnType.DEFAULT, title: 'Field Operator' },
      { key: 'expert', type: TableColumnType.DEFAULT, title: 'Remote Expert' },
      { key: 'created_at', type: TableColumnType.DATE, title: 'Creation date' },
      { key: 'closed_at', type: TableColumnType.DATE, title: 'Closed date' },
      { key: 'scheduled_at', type: TableColumnType.DATE, title: 'Schedule date' },
      { key: 'scheduled', type: TableColumnType.BOOLEAN, title: 'Is scheduled' },
      /*{ key: 'live_assistance_required', type: TableColumnType.BOOLEAN, title: 'Live assistance requested' },*/
      { key: 'is_bot', type: TableColumnType.BOOLEAN, title: 'Chatbot only' },
      // { key: 'actions', type: TableColumnType.ACTIONS, title: 'Actions' },
    ];
  }

  getMaintenanceTableData() {
    return this.maintenancesHistory.map(maintenance => {
      return {
        id: maintenance.id,
        equipment_name: maintenance.equipment.model.name,
        asset_serial: maintenance.equipment.serialNumber,
        field_operator: maintenance.fieldWorker?.name || '',
        expert: maintenance.remoteExpert?.name || '',
        created_at: new Date(maintenance.createdAt),
        closed_at: new Date(maintenance.closedAt),
        scheduled_at: maintenance.isExtraordinary ? null : new Date(maintenance.scheduledAt),
        scheduled: !maintenance.isExtraordinary,
        // live_assistance_required: maintenance.isSupportRequired,
        is_bot: maintenance.isBotEnabled,
        /*actions: [
          { icon: 'chat', callback: () => this.openChat(maintenance) }
        ] as TableAction[]*/
      };
    });
  }

  getPlannedMaintenanceTableColumns(): TableColumn[] {
    return [
      { key: 'id', type: TableColumnType.DEFAULT, title: 'ID', isMasterKey: true },
      { key: 'equipment_name', type: TableColumnType.DEFAULT, title: 'Equipment name' },
      { key: 'asset_serial', type: TableColumnType.DEFAULT, title: 'Serial number' },
      { key: 'field_operator', type: TableColumnType.DEFAULT, title: 'Field Operator' },
      { key: 'expert', type: TableColumnType.DEFAULT, title: 'Remote Expert' },
      { key: 'is_support_required', type: TableColumnType.BOOLEAN, title: 'Is expert required' },
      { key: 'status', type: TableColumnType.DEFAULT, title: 'Status' },
      { key: 'scheduled_at', type: TableColumnType.DATE, title: 'Schedule date' },
      { key: 'created_at', type: TableColumnType.DATE, title: 'Creation date' },
      { key: 'actions', type: TableColumnType.ACTIONS, title: 'Actions' },
    ];
  }

  getPlannedMaintenanceTableData() {
    return this.plannedMaintenances.map(maintenance => {
      return {
        id: maintenance.id,
        equipment_name: maintenance.equipment.model.name,
        asset_serial: maintenance.equipment.serialNumber,
        field_operator: maintenance.fieldWorker?.name || '',
        expert: maintenance.remoteExpert?.name || '',
        is_support_required: maintenance.isSupportRequired,
        status: this.translatePipe.transform(MaintenanceStatusUtils.toString(maintenance.status)),
        scheduled_at: new Date(maintenance.scheduledAt),
        created_at: new Date(maintenance.createdAt),
        actions: [
          {
            icon: 'assignment_ind',
            text: 'Assign',
            callback: () => {
              this.router.navigate(['calendar', 'event', maintenance.event.id])
            }
          }
        ] as TableAction[]
      };
    });
  }

  showMaintenance(maintenanceId: string, isPlanned: boolean) {
    this.openChat((isPlanned ? this.plannedMaintenances : this.maintenancesHistory).find(x => x.id == maintenanceId));
  }

  openChat(maintenance: Maintenance) {
    this.debugService.log(DEBUG.LIVE_ASSISTANCE, `Opening chat ${maintenance.id}`);
    this.router.navigate([maintenance.id], { relativeTo: this.route });
  }

  getDate(timestamp: number) {
    return new Date(timestamp);
  }

  startSupportMaintenanceRequest(maintenance: Maintenance) {

    if (maintenance.status == MaintenanceStatus.PLANNED) {
      this.uiManager.notify(this.translatePipe.transform('Wait for operator to start maintenance'));
      return;
    }

    // Block if assistance request is taken from another operator
    if (maintenance.status == 2 && maintenance.remoteExpert.id != this.accountManager.account.id) {
      this.uiManager.notify(this.translatePipe.transform('This request is already taken by another operator'));
      return;
    }

    const serveMaintenanceAction = async () => {

      // Show loading spinner
      this.isOpeningMaintenanceId = maintenance.id;

      // Update maintenance status
      await this.maintenanceManager.startSupportMaintenanceRequest(maintenance);

      // Update UI before websocket update (proactive? UI)
      maintenance.status = MaintenanceStatus.TAKEN;
      maintenance.remoteExpert = new Personnel();
      maintenance.remoteExpert.id = this.accountManager.account.id;

      // Hide loading spinner
      this.selectedMaintenanceId = maintenance.id;
      this.isOpeningMaintenanceId = null;

      this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Taking in charge maintenance', { maintenance: maintenance.id, event: maintenance.event.id });

    }

    // Block if assistance request is taken from another operator
    if (this.selectedMaintenanceId && this.selectedMaintenanceId != maintenance.id) {
      this.uiManager.showConfirmDialog('Switch maintenance', 'Do you want to switch maintenance to support? This will close current window.', () => {
        this.selectedMaintenanceId = null;
        serveMaintenanceAction();
      });
    } else if (this.selectedMaintenanceId != maintenance.id) {
      serveMaintenanceAction();
    }

  }

  getCurrentMaintenance(): Assistance {
    const maintenance = this.maintenances$.value.find(x => x.id == this.selectedMaintenanceId);

    // Override id with eventId (everything is managed by eventId)
    return { ...maintenance, id: maintenance.event.id, maintenance, procedure: maintenance.procedure };
  }

  onMaintenanceClosing() {
    // Remove local maintenance, do not wait websocket update
    this.maintenances$.next(this.maintenances$.value.filter(x => x.id != this.selectedMaintenanceId));
    this.selectedMaintenanceId = null;
  }

  onMaintenanceClosed() {
    // Update maintenance history and planned maintenances
    this.maintenancesPaginator.refresh();
    this.plannedMaintenancesPaginator?.refresh();
  }

  get AssistanceType() { return AssistanceType }

  get environment() { return environment }

}
